// Import necessary icons
import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import UploadModal from "./UploadModal";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import theme from "../themes/lightTheme";
import Grid from "@material-ui/core/Grid";
import ConfirmDialog from "./ConfirmDialog";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import SettingsIcon from "@material-ui/icons/Settings";
import templateService from "../services/TemplateService";
import notify from "../utils/notifier";
import { useTranslation } from "react-i18next";
import Util from "../utils/Util";
import Tooltip from "@material-ui/core/Tooltip";
import GenerateSampleModal from "./GenerateSampleModal";
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
// Import privileges utility
import { hasPrivilege } from "../utils/privileges_utility";

//TODO: this columns to be fetched form api once api supports the same...
export const _columns = [
  { id: "templateId", label: "ID", minWidth: 170, sortable: true },
  { id: "templateName", label: "Name", minWidth: 100, sortable: true },
  {
    id: "organizationId",
    label: "Organization",
    minWidth: 170,
    sortable: true
  },
  { id: "client", label: "Client", minWidth: 80 },
  { id: "applicationId", label: "Application", minWidth: 170, sortable: true },
  { id: "vendor", label: "Preferred Vendor", minWidth: 120, sortable: true }, // Added the vendor column
  { id: "apryseTested", label: "Apryse Tested", minWidth: 120, sortable: true },
  { id: "updatedAt", label: "Updated At", minWidth: 170, sortable: true },
  { id: "action_column", label: "Actions", minWidth: 170 }
];

//TODO: userPrefs should be fetched from user-session context
export const userPrefs = {
  rowsPerPageOptions: [10, 25, 100]
};
const useStyles = makeStyles({
  root: {
    width: "100%",
    background: "white"
  },
  tableWrapper: {
    width: "100%",
    maxHeight: "70vh",
    overflow: "auto"
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  },
  tableHeadRight: {
    float: "right",
    display: "flex"
  },
  tableTitle: {
    float: "left",
    paddingLeft: "20px"
  },
  paper: {
    width: "45%",
    color: theme.secondaryColor,
    background: theme.primaryColor
  },
  displayFlex: {
    display: "flex"
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1
  },
  disabledIcon: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  workingIcon: {
    color: '#4caf50', // Green
    verticalAlign: 'middle'
  },
  notWorkingIcon: {
    color: '#f44336', // Red
    verticalAlign: 'middle'
  },
  notYetTestedIcon: {
    color: '#ffcf33', // Yellow
    verticalAlign: 'middle'
  },
  apryseTestedText: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  }
});
const EDIT_MODE = "edit";
const CREATE_MODE = "create";

export default function Template({ currentCompany }) {
  const [t, i18n] = useTranslation();
  const classes = useStyles();
  const [backdrop, setBackdrop] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [originalRows, setOriginalRows] = React.useState([]);
  const [queryCount, setQueryCount] = useState(0);
  const [columns, setColumns] = React.useState(_columns);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [value, setValue] = React.useState("");
  const [originalQueryCount, setOriginalQueryCount] = useState(0);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("templateId");

  const [searchKey, setSearchKey] = useState({ value: "" });

  // Check user privileges
  const canUpload = hasPrivilege("DOC_TEMP_UPLOAD");
  const canDownload = hasPrivilege("DOC_TEMP_DOWNLOAD");
  const canGenerate = hasPrivilege("DOC_TEMP_GENERATE");
  const canDelete = hasPrivilege("DOC_TEMP_DEL");
  const canAmend = hasPrivilege("DOC_TEMP_AMND");

  const handleRequestSort = property => event => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
    let _val=searchKey.value;
    fetchData(rowsPerPage, page, property, isDesc ? "asc" : "desc", _val);

  };

  const fetchData = (pageSize = rowsPerPage, currentPage = 0, sortBy = orderBy, sortOrder = order, searchText = null) => {
    setBackdrop(true);
    let query = {
      '_page': currentPage + 1,
      '_limit': pageSize,
      '_sort': sortBy,
      '_order': sortOrder,
      '_fileExtension': '.docx,.doc,.zpl'
    }

    if (searchText) {
      query['q'] = searchText;
    }

    templateService.getAllTemplates(query).then(
      response => {
        setBackdrop(true)
        let _data = response.data;
        _data.map(x=>{
          x.updatedAt=new Date(x.updatedAt).toLocaleString();
          
          // Add vendor field based on docxPdfStatergy
          if (x.docxPdfStatergy) {
            x.vendor = x.docxPdfStatergy === "pdfTron" ? "apryse" : x.docxPdfStatergy;
          } else {
            x.vendor = ""; // Default value if docxPdfStatergy is not defined
          }
        });
        setRows(_data);
        if (response.headers.hasOwnProperty('x-total-count')) {
          setQueryCount(parseInt(response.headers['x-total-count']));
        }
      
        if (!searchText) {
          setOriginalRows(_data);
          setOriginalQueryCount(parseInt(response.headers['x-total-count']));
        }
        setBackdrop(false);
      },
      error => {
        setBackdrop(false);
        notify({
          type: "error",
          message: "Error occured while fetcing the data"
        });
      }
    );
  };

  React.useEffect(() => {
    if (currentCompany) {
      //fetch all templates on load
      fetchData();
    }
  }, [currentCompany]);

  const download = id => {
    if (!canDownload) {
      notify({
        type: "error",
        message: "You don't have permission to download templates"
      });
      return;
    }
    
    templateService.downloadTemplate(id).then(
      response => {
        Util.downloadTemplate(response);
      },
      error => {
        notify({
          type: "error",
          message: "Error: " + error.response.data.errorCode
        });
      }
    );
  };

  const deleteRecord = id => {
    if (!canDelete) {
      notify({
        type: "error",
        message: "You don't have permission to delete templates"
      });
      return;
    }
    
    templateService.deleteTemplate(id).then(
      response => {
        refreshTableDelete(id);
        notify({
          type: "success",
          message: "Template " + id + " has been deleted"
        });
      },
      error => {
        notify({
          type: "error",
          message: "Error: " + error.response.data.errorCode
        });
      }
    );
  };

  const refreshTableDelete = rowId => {
    const index = rows.map(e => e.templateId).indexOf(rowId);
    const newRows = [].concat(rows);
    newRows.splice(index, 1);
    setRows(newRows);
    if(newRows.length==0){
      if(page!=0){
        //if it is not first page, when there are no rows it will get redirected to previous page
        setPage(page-1);
        fetchData(rowsPerPage, page-1, orderBy, order, searchKey.value);
      }
      else fetchData(rowsPerPage, 0, orderBy, order, searchKey.value);
    }
    else fetchData(rowsPerPage,page, orderBy, order, searchKey.value);
  };
  const refreshTableAdd = row => {
    const newRows = [].concat(rows);
    newRows.push(row.data);
    setRows(newRows);
    fetchData(rowsPerPage,page, orderBy, order, searchKey.value);
  };
  const refreshTableUpdate = row => {
    const newRows = [].concat(rows);
    let objIndex = newRows.findIndex(obj => obj.templateId === row.recordId);
    row.data["templateId"] = row.recordId;
        row.data["metadata"] = JSON.parse(row.data["metadata"]);
    newRows[objIndex] = row.data;
    setRows(newRows);
    fetchData(rowsPerPage,page, orderBy, order, searchKey.value);
  };

  const handleDialogResponse = result => {
    if (result.status === "SUCCESS") {
      deleteRecord(result.data);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(rowsPerPage, newPage, orderBy, order, searchKey.value);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    fetchData(+event.target.value, 0, orderBy, order, searchKey.value);
  };

  const [open, setOpen] = React.useState(false);
  const handleClickListItem = () => {
    setOpen(true);
  };

  const handleClose = newValue => {
    setOpen(false);
    if (newValue) {
      setValue(newValue);
    }
  };

  const clearSearchText = () => {
    setSearchKey({ value: "" });
    fetchData(rowsPerPage, 0, orderBy, 'desc', "");
  };

  let timeout = null;
  const handleSearch = event => {
    const _val = event.target.value;
    setSearchKey(prevState => {
      return { ...prevState, value: _val };
    });
    if (_val.length <= 2) {
      setRows(originalRows);
      setQueryCount(originalQueryCount);
      return;
    }
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      setPage(0);
      fetchData(rowsPerPage, 0, orderBy, order, _val);
    }, 200);
  };

  const handleUploadedData = result => {
    setOpen(true);
    setBackdrop(true);
    if (result.mode === "create") {
      refreshTableAdd(result);
    } else {
      refreshTableUpdate(result);
    }
    setBackdrop(false);
  };
  
  // Render Upload button conditionally based on privileges
  const renderUploadButton = () => {
    if (canUpload) {
      return (
        <UploadModal
          onUploadComplete={handleUploadedData}
          mode={CREATE_MODE}
        />
      );
    }
    return null;
  };

  // Define Apryse tested status constants
  const ApryseTestedStatus = {
    NOT_YET_TESTED: 'not yet tested',
    WORKING: 'working',
    NOT_WORKING: 'not working',
  };

  // Render the apryseTested cell content
  const renderApryseTestedCell = (value) => {
    // If value is undefined or null, default to NOT_YET_TESTED
    const status = value || ApryseTestedStatus.NOT_YET_TESTED;
    
    // Convert to lowercase for case-insensitive comparison
    const statusLower = typeof status === 'string' ? status.toLowerCase() : '';
    
    switch (statusLower) {
      case ApryseTestedStatus.WORKING:
        return (
          <Tooltip title="Template works with Apryse" placement="top">
            <div className={classes.apryseTestedText}>
              <span>Working</span>
              <CheckCircleIcon className={classes.workingIcon} fontSize="small" />
            </div>
          </Tooltip>
        );
      
      case ApryseTestedStatus.NOT_WORKING:
        return (
          <Tooltip title="Template does not work with Apryse" placement="top">
            <div className={classes.apryseTestedText}>
              <span>Not Working</span>
              <CancelIcon className={classes.notWorkingIcon} fontSize="small" />
            </div>
          </Tooltip>
        );
      
      case ApryseTestedStatus.NOT_YET_TESTED:
        return (
          <Tooltip title="Template has not been tested with Apryse yet" placement="top">
            <div className={classes.apryseTestedText}>
              <span>Not Yet Tested</span>
              <HelpOutlineIcon className={classes.notYetTestedIcon} fontSize="small" />
            </div>
          </Tooltip>
        );
      
      default:
        // For any unexpected values, treat as NOT_YET_TESTED
        return (
          <Tooltip title="Template has not been tested with Apryse yet" placement="top">
            <div className={classes.apryseTestedText}>
              <span>Not Yet Tested</span>
              <HelpOutlineIcon className={classes.notYetTestedIcon} fontSize="small" />
            </div>
          </Tooltip>
        );
    }
  };

  // Render action buttons conditionally based on privileges
  const renderActionButtons = (rowId, row) => {
    return (
      <div className={classes.displayFlex}>
        {/* Generate Sample Button */}
        {canGenerate ? (
          <GenerateSampleModal
            rowId={rowId}
            row={Util.copyOf(row)}
          />
        ) : (
          <Tooltip title="Generate Sample (Requires permission)" placement="top">
            <span className={classes.disabledIcon}>
              <IconButton
                aria-label="generate"
                color="primary"
                size="small"
                disabled={true}
              >
                <SettingsIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Download Button */}
        {canDownload ? (
          <Tooltip title="Download" placement="top">
            <IconButton
              aria-label="download"
              color="primary"
              size="small"
              onClick={event => {
                download(rowId, event);
              }}
            >
              <CloudDownloadIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Download (Requires permission)" placement="top">
            <span className={classes.disabledIcon}>
              <IconButton
                aria-label="download"
                color="primary"
                size="small"
                disabled={true}
              >
                <CloudDownloadIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Edit Button */}
        {canAmend ? (
          <UploadModal
            onUploadComplete={handleUploadedData}
            mode={EDIT_MODE}
            data={row}
          />
        ) : null}

        {/* Delete Button */}
        {canDelete ? (
          <ConfirmDialog
            dialogResultCallBack={handleDialogResponse}
            recordId={rowId}
          />
        ) : null}
      </div>
    );
  };
  
  return (
    <Paper className={classes.root}>
      <Backdrop open={backdrop} style={{ color: "primary", zIndex: 1000000 }} >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid item xs={12}>
        <div className={classes.tableTitle}>
          <h3>{t("Docx Templates")}</h3>
        </div>
        <div className={classes.tableHeadRight}>
          <div className={classes.searchBox}>
            <InputBase
              onChange={handleSearch}
              value={searchKey.value}
              className={classes.input}
              placeholder={t("Search Templates")}
            />
            {searchKey.value.length ? (
              <IconButton
                size="medium"
                aria-label="search"
                onClick={clearSearchText}
              >
                <CloseIcon />
              </IconButton>
            ) : (
                ""
              )}
            <IconButton
              size="medium"
              aria-label="search"
              // onClick={handleSearch}
              disabled
            // disabled={!searchKey.value.length}
            >
              <SearchIcon />
            </IconButton>
          </div>
          {renderUploadButton()}
        </div>
        <div className={classes.tableWrapper}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.id}
                    align={column.numeric ? "right" : "left"}
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={order}
                        onClick={handleRequestSort(column.id)}
                      >
                        {t(column.label)}
                        {orderBy === column.id ? (
                          <span className={classes.visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                        t(column.label)
                      )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                  const rowId = row["templateId"];
                  return (
                    <TableRow hover role="data-row" tabIndex={-1} key={index}>
                      {columns.map(column => {
                        const value = row[column.id];
                        const isActionCell = column.id === "action_column";
                        const isApryseTestedCell = column.id === "apryseTested";
                        
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {isActionCell ? (
                              renderActionButtons(rowId, row)
                            ) : isApryseTestedCell ? (
                              renderApryseTestedCell(value)
                            ) : column.format && typeof value === "number" ? (
                              column.format(value)
                            ) : (
                                  value
                                )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={userPrefs.rowsPerPageOptions}
          component="div"
          count={queryCount}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            "aria-label": "previous page"
          }}
          nextIconButtonProps={{
            "aria-label": "next page"
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage={t("Rows per page")}
        />
      </Grid>
    </Paper>
  );
}